import { UserManager, WebStorageStateStore, Log } from 'oidc-client';
import { ApplicationPaths, ApplicationName } from './ApiAuthorizationConstants';

// Enable oidc-client debug logging
Log.logger = console;
Log.level = Log.DEBUG;

export const AuthenticationResultStatus = {
    Redirect: 'redirect',
    Success: 'success',
    Fail: 'fail'
};

export class AuthorizeService {
    _callbacks = [];
    _nextSubscriptionId = 0;
    _user = null;
    _isAuthenticated = false;

    // By default pop ups are disabled because they don't work properly on Edge.
    // If you want to enable pop up authentication simply set this flag to false.
    _popUpDisabled = true;

    //https://stackoverflow.com/questions/61201244/identify-roles-with-spa-and-net-core-3
 
    async isAuthenticated() {
        const user = await this.getUser();
        return !!user;
    }

    async getUser() {
        if (this._user && this._user.profile) {
            return this._user.profile;
        }

        try {
            await this.ensureUserManagerInitialized();
            const user = await this.userManager.getUser();
            return user && user.profile;
        } catch (error) {
            console.error('Error getting user:', error);
            return null;
        }
    }

    //https://github.com/dotnet/aspnetcore/issues/17962
    //https://levelup.gitconnected.com/react-template-for-jwt-authentication-with-private-routes-and-redirects-f77c488bfb85
    async getAccessToken() {
        try {
            await this.ensureUserManagerInitialized();
            const user = await this.userManager.getUser();
            if (user == null)
                return null;
            if (user.expired === true) {
                try {
                    //Attempt to sign in our user silently
                    //const silentUser = await this.userManager.signinSilent(this.createArguments(LoginMode.Silent))
                    const silentUser = await this.userManager.signinSilent(this.createArguments())
                    //Return refreshed access token
                    return silentUser && silentUser.access_token
                } catch (err) {
                    //Failed to sign in user silently
                    // Check if it's a login_required error
                    if (this.isLoginRequiredError(err)) {
                        console.log("Silent sign-in failed with login_required, redirecting to login");
                        try {
                            await this.signIn();
                        } catch (redirectError) {
                            console.log("Redirect authentication error: ", redirectError);
                        }
                    } else {
                        console.error("Silent token renewal error:", err);
                        try {
                            await this.signIn();
                        } catch (redirectError) {
                            console.log("Redirect authentication error: ", redirectError);
                        }
                    }
                }
            }
            return user && user.access_token;
        } catch (error) {
            console.error('Error getting access token:', error);
            return null;
        }
    }

    // Helper method to identify login_required errors
    isLoginRequiredError(error) {
        if (!error) return false;
        
        const errorMessage = error.message || error.error || '';
        const loginRequiredErrors = [
            'login_required',
            'consent_required',
            'interaction_required',
            'account_selection_required'
        ];
        
        return loginRequiredErrors.some(err => errorMessage.toLowerCase().includes(err.toLowerCase()));
    }

    // We try to authenticate the user in three different ways:
    // 1) We try to see if we can authenticate the user silently. This happens
    //    when the user is already logged in on the IdP and is done using a hidden iframe
    //    on the client.
    // 2) We try to authenticate the user using a PopUp Window. This might fail if there is a
    //    Pop-Up blocker or the user has disabled PopUps.
    // 3) If the two methods above fail, we redirect the browser to the IdP to perform a traditional
    //    redirect flow.
    async signIn(state) {
        try {
            await this.ensureUserManagerInitialized();
        } catch (error) {
            console.error('Failed to initialize user manager:', error);
            return this.error('Failed to initialize authentication. Please check your configuration and network connection.');
        }

        try {
            const silentUser = await this.userManager.signinSilent(this.createArguments());
            this.updateState(silentUser);
            return this.success(state);
        } catch (silentError) {
            // User might not be authenticated, fallback to popup authentication
            console.log("Silent authentication error: ", silentError);

            // Check if it's a login_required error - if so, skip popup and go straight to redirect
            if (this.isLoginRequiredError(silentError)) {
                console.log("Login required, redirecting to login page");
                try {
                    await this.userManager.signinRedirect(this.createArguments(state));
                    return this.redirect();
                } catch (redirectError) {
                    console.log("Redirect authentication error: ", redirectError);
                    return this.error(redirectError);
                }
            }

            try {
                if (this._popUpDisabled) {
                    throw new Error('Popup disabled. Change \'AuthorizeService.js:AuthorizeService._popupDisabled\' to false to enable it.')
                }

                const popUpUser = await this.userManager.signinPopup(this.createArguments());
                this.updateState(popUpUser);
                return this.success(state);
            } catch (popUpError) {
                if (popUpError.message === "Popup window closed") {
                    // The user explicitly cancelled the login action by closing an opened popup.
                    return this.error("The user closed the window.");
                } else if (!this._popUpDisabled) {
                    console.log("Popup authentication error: ", popUpError);
                }

                // PopUps might be blocked by the user, fallback to redirect
                try {
                    await this.userManager.signinRedirect(this.createArguments(state));
                    return this.redirect();
                } catch (redirectError) {
                    console.log("Redirect authentication error: ", redirectError);
                    return this.error(redirectError);
                }
            }
        }
    }

    async completeSignIn(url) {
        try {
            await this.ensureUserManagerInitialized();
        } catch (error) {
            console.error('Failed to initialize user manager during callback:', error);
            return this.error('Failed to initialize authentication. Please check that the identity server is accessible.');
        }

        try {
            const user = await this.userManager.signinCallback(url);
            this.updateState(user);
            return this.success(user && user.state);
        } catch (error) {
            console.error('There was an error signing in: ', error);
            
            // Check if it's a login_required error
            if (this.isLoginRequiredError(error)) {
                return this.error('Login required. Please sign in again.');
            }
            
            return this.error('There was an error signing in.');
        }
    }

    // We try to sign out the user in two different ways:
    // 1) We try to do a sign-out using a PopUp Window. This might fail if there is a
    //    Pop-Up blocker or the user has disabled PopUps.
    // 2) If the method above fails, we redirect the browser to the IdP to perform a traditional
    //    post logout redirect flow.
    async signOut(state) {
        try {
            await this.ensureUserManagerInitialized();
        } catch (error) {
            console.error('Failed to initialize user manager:', error);
            return this.error('Failed to initialize authentication.');
        }

        try {
            if (this._popUpDisabled) {
                throw new Error('Popup disabled. Change \'AuthorizeService.js:AuthorizeService._popupDisabled\' to false to enable it.')
            }

            await this.userManager.signoutPopup(this.createArguments());
            this.updateState(undefined);
            return this.success(state);
        } catch (popupSignOutError) {
            console.log("Popup signout error: ", popupSignOutError);
            try {
                await this.userManager.signoutRedirect(this.createArguments(state));
                return this.redirect();
            } catch (redirectSignOutError) {
                console.log("Redirect signout error: ", redirectSignOutError);
                return this.error(redirectSignOutError);
            }
        }
    }

    async completeSignOut(url) {
        try {
            await this.ensureUserManagerInitialized();
        } catch (error) {
            console.error('Failed to initialize user manager:', error);
            return this.error('Failed to initialize authentication.');
        }

        try {
            const response = await this.userManager.signoutCallback(url);
            this.updateState(null);
            return this.success(response && response.data);
        } catch (error) {
            console.log(`There was an error trying to log out '${error}'.`);
            return this.error(error);
        }
    }

    updateState(user) {
        this._user = user;
        this._isAuthenticated = !!this._user;
        this.notifySubscribers();
    }

    subscribe(callback) {
        this._callbacks.push({ callback, subscription: this._nextSubscriptionId++ });
        return this._nextSubscriptionId - 1;
    }

    unsubscribe(subscriptionId) {
        const subscriptionIndex = this._callbacks
            .map((element, index) => element.subscription === subscriptionId ? { found: true, index } : { found: false })
            .filter(element => element.found === true);
        if (subscriptionIndex.length !== 1) {
            throw new Error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
        }

        this._callbacks.splice(subscriptionIndex[0].index, 1);
    }

    notifySubscribers() {
        for (let i = 0; i < this._callbacks.length; i++) {
            const callback = this._callbacks[i].callback;
            callback();
        }
    }

    createArguments(state) {
        return { useReplaceToNavigate: true, data: state };
    }

    error(message) {
        return { status: AuthenticationResultStatus.Fail, message };
    }

    success(state) {
        return { status: AuthenticationResultStatus.Success, state };
    }

    redirect() {
        return { status: AuthenticationResultStatus.Redirect };
    }

    async ensureUserManagerInitialized() {
        if (this.userManager !== undefined) {
            return;
        }

        console.log('Fetching OIDC client configuration from:', ApplicationPaths.ApiAuthorizationClientConfigurationUrl);

        let response;
        try {
            response = await fetch(ApplicationPaths.ApiAuthorizationClientConfigurationUrl);
        } catch (fetchError) {
            console.error('Network error fetching configuration:', fetchError);
            throw new Error(`Could not connect to server: ${fetchError.message}`);
        }

        if (!response.ok) {
            console.error('HTTP error fetching configuration. Status:', response.status);
            throw new Error(`Could not load settings for '${ApplicationName}' (HTTP ${response.status})`);
        }

        let settings;
        try {
            settings = await response.json();
            console.log('OIDC configuration loaded successfully:', settings);
        } catch (parseError) {
            console.error('Error parsing configuration:', parseError);
            throw new Error(`Invalid configuration format: ${parseError.message}`);
        }

        // Disable automatic silent renew to prevent background errors
        settings.automaticSilentRenew = false;
        settings.includeIdTokenInSilentRenew = true;
        settings.userStore = new WebStorageStateStore({
            prefix: ApplicationName
        });

        console.log('Creating UserManager with authority:', settings.authority);

        try {
            this.userManager = new UserManager(settings);
            console.log('UserManager created successfully');
        } catch (managerError) {
            console.error('Error creating UserManager:', managerError);
            throw new Error(`Failed to create UserManager: ${managerError.message}`);
        }

        this.userManager.events.addUserSignedOut(async () => {
            await this.userManager.removeUser();
            this.updateState(undefined);
        });

        // Add error handler for silent renew errors
        this.userManager.events.addSilentRenewError((error) => {
            console.error('Silent renew error:', error);
            // Optionally redirect to login if needed
            // this.signIn({ returnUrl: window.location.pathname });
        });

        console.log('UserManager initialization complete. It will now try to load OIDC discovery document from:', settings.authority + '/.well-known/openid-configuration');
    }

    static get instance() { return authService }

}

const authService = new AuthorizeService();

export default authService;
